// Zonas de códigos postales (pedido de Nico, 26-sep). Una zona es un nombre
// («Vicente López», «Zona Norte») con su lista de CP, que después se vincula
// a estanterías y a tarifas en vez de escribir los CP a mano en cada una.
// Desde el 13-oct también puede tener polígonos dibujados en el mapa (donde
// los CP no alcanzan): en la estantería, un bulto con coordenadas que cae
// adentro va por el polígono antes que por el CP. Si la Configuración lo
// pide, los polígonos también cuentan para cobrar, para liquidar y para
// sugerir el chofer del reparto (la zona puede tener choferes habituales).
//
// Lo que la pantalla tiene que dejar claro: editar los CP de una zona cambia
// en el acto todo lo que la tiene vinculada (y lo informa), y una zona en uso
// no se borra.

import React, { useCallback, useEffect, useRef, useState } from "react";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";
import { useAuth } from "../context/AuthContext";
import { authFetch } from "../lib/authFetch";
import ZonePolygonEditor, { MapPoint, OtherPolygon } from "../components/zones/ZonePolygonEditor";
import { Ring } from "../components/zones/polygonEditing";
import DriverMultiPicker, { DriverOption } from "../components/zones/DriverMultiPicker";
import ZoneSettingsForm from "../components/zones/ZoneSettingsForm";

type ZoneRow = { id: number; name: string; description: string | null; updated_at: string; codes_count: number; polygons_count: number; shelves_count: number; tariffs_count: number; drivers_count?: number };
type Zone = {
  id: number; name: string; description: string | null; codes: string[]; polygons: Ring[]; drivers?: { driver_id: number }[];
  links: { shelves: { shelf_id: number; code: string; name: string }[]; tariffs: { tariff_id: number; name: string | null; type: string; tier: string }[] };
};
type Synced = { kind: "shelf" | "tariff"; id: number; label: string; added: string[]; removed: string[]; adopted: string[]; moved?: { postal_code: string; from: string }[]; retiered?: { postal_code: string; from: string; to: string }[]; affected?: number };
type SaveResult = { zone: Zone; unknown: string[]; empty_ranges: string[]; synced: Synced[] };

const titleStyle: React.CSSProperties = { margin: 0, fontSize: "24px", fontWeight: 700, color: "var(--text-primary)" };
const subtitleStyle: React.CSSProperties = { margin: "8px 0 0 0", color: "var(--text-secondary)", fontSize: "15px", lineHeight: 1.6 };
const smallMutedStyle: React.CSSProperties = { fontSize: "12px", color: "var(--text-secondary)" };
const tableStyle: React.CSSProperties = { width: "100%", borderCollapse: "collapse", fontSize: "14px" };
const thStyle: React.CSSProperties = { textAlign: "left", padding: "10px 12px", borderBottom: "1px solid var(--border)", color: "var(--text-secondary)", fontWeight: 600, fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.04em", whiteSpace: "nowrap" };
const tdStyle: React.CSSProperties = { padding: "10px 12px", borderBottom: "1px solid var(--border)", color: "var(--text-primary)", verticalAlign: "top" };
const panelStyle: React.CSSProperties = { border: "1px solid var(--border)", borderRadius: "18px", background: "rgba(255,255,255,0.03)", padding: "14px" };
const textareaStyle: React.CSSProperties = { width: "100%", minHeight: "150px", padding: "12px 14px", borderRadius: "16px", border: "1px solid var(--border)", background: "var(--surface-alt)", color: "var(--text-primary)", fontFamily: "monospace", fontSize: "14px" };
const WARN = "var(--warning, #d29922)";

const plural = (n: number, singular: string, plural_: string) => `${n} ${n === 1 ? singular : plural_}`;
const fmtDate = (iso: string | null) => (iso ? new Date(iso).toLocaleString("es-AR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");

// Se cierra con un clic en el fondo solo si el clic EMPEZÓ en el fondo:
// arrastrar un vértice o el mapa y soltar afuera no cierra nada.
function Modal({ title, onClose, children, width = "820px" }: { title: React.ReactNode; onClose: () => void; children: React.ReactNode; width?: string }) {
  const downOnBackdrop = useRef(false);
  return (
    <div
      data-testid="modal-backdrop"
      onMouseDown={(e) => { downOnBackdrop.current = e.target === e.currentTarget; }}
      onClick={(e) => { if (downOnBackdrop.current && e.target === e.currentTarget) onClose(); downOnBackdrop.current = false; }}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "40px 16px", zIndex: 100, overflowY: "auto" }}>
      <div style={{ width: "100%", maxWidth: width }}>
        <Card>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
            <h3 style={{ margin: 0, fontSize: "18px", color: "var(--text-primary)" }}>{title}</h3>
            <Button variant="ghost" onClick={onClose}>Cerrar</Button>
          </div>
          {children}
        </Card>
      </div>
    </div>
  );
}

// Qué cambió en cada estantería/tarifa vinculada al guardar la zona.
export function SyncReport({ synced }: { synced: Synced[] }) {
  const changed = synced.filter((s) => s.added.length || s.removed.length || s.adopted.length || s.moved?.length || s.retiered?.length || s.affected);
  if (!synced.length) return null;
  if (!changed.length) return <div style={smallMutedStyle}>Lo vinculado ya estaba al día: no cambió nada.</div>;
  return (
    <div style={{ display: "grid", gap: "6px" }}>
      {changed.map((s) => (
        <div key={`${s.kind}-${s.id}`}>
          <b>{s.kind === "shelf" ? "Estantería" : "Tarifa"} {s.label}</b>:{" "}
          {[
            s.added.length ? `se agregaron ${s.added.join(", ")}` : null,
            s.removed.length ? `se quitaron ${s.removed.join(", ")}` : null,
            s.adopted.length ? `pasaron a la zona ${s.adopted.join(", ")} (estaban a mano)` : null,
            s.retiered?.length ? `cambiaron de tier ${s.retiered.map((r) => `${r.postal_code} ${r.from}→${r.to}`).join(", ")}` : null,
            s.moved?.length ? `se movieron de otra estantería ${s.moved.map((m) => `${m.postal_code} (de ${m.from})`).join(", ")}` : null,
          ].filter(Boolean).join(" · ")}
          {s.affected ? <div style={{ color: WARN }}>{plural(s.affected, "paquete ya estanteado quedó", "paquetes ya estanteados quedaron")} en la estantería vieja: hay que moverlos a mano.</div> : null}
        </div>
      ))}
    </div>
  );
}

export default function ZonesPage() {
  const { token } = useAuth();
  const req = useCallback(async (path: string, init: { method?: string; body?: unknown } = {}) => {
    const response = await authFetch(path, { token, method: init.method ?? "GET", json: init.body !== undefined, body: init.body === undefined ? undefined : JSON.stringify(init.body) });
    const json = await response.json().catch(() => ({}));
    if (!response.ok || json?.ok === false) {
      const err: any = new Error(json?.error || `Error ${response.status}`);
      if (json?.unknown) err.unknown = json.unknown;
      throw err;
    }
    return json;
  }, [token]);

  const [zones, setZones] = useState<ZoneRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [editing, setEditing] = useState<number | "new" | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setZones((await req("/v2/zones")).zones ?? []); } catch (e: any) { setError(e.message); } finally { setLoading(false); }
  }, [req]);
  useEffect(() => { load(); }, [load]);

  const flash = (msg: string) => { setNotice(msg); window.setTimeout(() => setNotice(null), 6000); };

  const remove = async (z: ZoneRow) => {
    if (!window.confirm(`¿Borrar la zona «${z.name}»?`)) return;
    try { await req(`/v2/zones/${z.id}`, { method: "DELETE" }); flash(`Zona «${z.name}» borrada`); load(); } catch (e: any) { setError(e.message); }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", gap: "16px", flexWrap: "wrap", alignItems: "flex-start" }}>
          <div>
            <h2 style={titleStyle}>Zonas</h2>
            <p style={subtitleStyle}>
              Grupos de códigos postales con nombre («Vicente López», «Zona Norte»). Se vinculan a estanterías y a listas de precios
              para no cargar los CP a mano en cada una: al cambiar los CP de una zona, cambia todo lo que la tiene vinculada.
              Donde los CP no alcanzan, la zona se dibuja en el mapa: en las estanterías, un paquete con ubicación que cae en el
              polígono va por el polígono antes que por el CP. En «Configuración» se decide si los polígonos también cuentan para
              cobrar, para liquidar y para sugerir el chofer del reparto.
            </p>
          </div>
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            <Button variant="secondary" onClick={() => setSettingsOpen(true)}>Configuración</Button>
            <Button onClick={() => setEditing("new")}>Nueva zona</Button>
          </div>
        </div>
      </Card>

      {error ? <div style={{ ...panelStyle, borderColor: "var(--danger)", color: "var(--danger)" }}>{error} <Button variant="ghost" onClick={() => setError(null)}>Cerrar</Button></div> : null}
      {notice ? <div style={{ ...panelStyle, borderColor: "var(--success)", color: "var(--success)" }}>{notice}</div> : null}

      <Card>
        <div style={{ overflowX: "auto" }}>
          <table style={tableStyle}>
            <thead><tr><th style={thStyle}>Zona</th><th style={thStyle}>CP</th><th style={thStyle}>Polígonos</th><th style={thStyle}>Vinculada a</th><th style={thStyle}>Última modificación</th><th style={thStyle}></th></tr></thead>
            <tbody>
              {loading && !zones.length ? <tr><td style={tdStyle} colSpan={6}>Cargando…</td></tr>
                : !zones.length ? <tr><td style={tdStyle} colSpan={6}>Todavía no hay zonas. Creá la primera con «Nueva zona».</td></tr>
                : zones.map((z) => (
                  <tr key={z.id}>
                    <td style={tdStyle}><b>{z.name}</b>{z.description ? <div style={smallMutedStyle}>{z.description}</div> : null}</td>
                    <td style={tdStyle}>{z.codes_count}</td>
                    <td style={tdStyle}>{z.polygons_count || <span style={smallMutedStyle}>—</span>}</td>
                    <td style={tdStyle}>
                      {z.shelves_count || z.tariffs_count
                        ? [z.shelves_count ? plural(z.shelves_count, "estantería", "estanterías") : null, z.tariffs_count ? plural(z.tariffs_count, "tarifa", "tarifas") : null].filter(Boolean).join(" · ")
                        : <span style={smallMutedStyle}>sin vincular</span>}
                      {z.drivers_count ? <div style={smallMutedStyle}>{plural(z.drivers_count, "chofer habitual", "choferes habituales")}</div> : null}
                    </td>
                    <td style={tdStyle}>{fmtDate(z.updated_at)}</td>
                    <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                      <div style={{ display: "flex", gap: "8px" }}>
                        <Button variant="secondary" onClick={() => setEditing(z.id)}>Editar</Button>
                        <Button variant="ghost" onClick={() => remove(z)} disabled={z.shelves_count + z.tariffs_count > 0}>Borrar</Button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </Card>

      {settingsOpen ? (
        <Modal title="Configuración de polígonos" onClose={() => setSettingsOpen(false)} width="760px">
          <ZoneSettingsForm req={req} onSaved={() => flash("Configuración guardada")} />
        </Modal>
      ) : null}

      {editing !== null ? (
        <ZoneModal
          zoneId={editing === "new" ? null : editing}
          req={req}
          onClose={() => setEditing(null)}
          onSaved={(msg) => { flash(msg); load(); }}
        />
      ) : null}
    </div>
  );
}

function snapshotOf(name: string, description: string, codes: string, polygons: Ring[], driverIds: number[]) {
  return JSON.stringify([name, description, codes, polygons, [...driverIds].sort((a, b) => a - b)]);
}

function ZoneModal({ zoneId, req, onClose, onSaved }: { zoneId: number | null; req: any; onClose: () => void; onSaved: (msg: string) => void }) {
  const [zone, setZone] = useState<Zone | null>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [codes, setCodes] = useState("");
  const [polygons, setPolygons] = useState<Ring[]>([]);
  const [driverIds, setDriverIds] = useState<number[]>([]);
  const [drivers, setDrivers] = useState<DriverOption[]>([]);
  const [others, setOthers] = useState<OtherPolygon[]>([]);
  const [points, setPoints] = useState<MapPoint[]>([]);
  const [loading, setLoading] = useState(zoneId !== null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingUnknown, setPendingUnknown] = useState<string[] | null>(null);
  const [result, setResult] = useState<SaveResult | null>(null);
  const [drawing, setDrawing] = useState(false);
  // Lo último guardado (o cargado): para saber si hay cambios sin guardar.
  const savedSnapshot = useRef(snapshotOf("", "", "", [], []));

  const apply = (z: Zone) => {
    const ids = (z.drivers ?? []).map((d) => d.driver_id);
    setZone(z); setName(z.name); setDescription(z.description ?? ""); setCodes(z.codes.join("\n")); setPolygons(z.polygons ?? []);
    setDriverIds(ids);
    savedSnapshot.current = snapshotOf(z.name, z.description ?? "", z.codes.join("\n"), z.polygons ?? [], ids);
  };
  const dirty = snapshotOf(name, description, codes, polygons, driverIds) !== savedSnapshot.current || drawing;
  // Cerrar con cambios sin guardar (o con un polígono a medio dibujar) pregunta.
  const close = () => {
    if (dirty && !window.confirm("Hay cambios sin guardar en la zona. ¿Cerrar y perderlos?")) return;
    onClose();
  };

  // Fondo del mapa: si falla, se dibuja igual (sin referencias).
  useEffect(() => {
    req("/v2/zones/polygons").then((r: any) => setOthers((r.polygons ?? []).filter((p: OtherPolygon) => p.zone_id !== zoneId))).catch(() => {});
    req("/v2/zones/map-points").then((r: any) => setPoints((r.points ?? []).map((p: any) => ({ lat: Number(p.lat), lng: Number(p.lng) })))).catch(() => {});
    req("/v2/routes/drivers").then((r: any) => setDrivers(r.drivers ?? [])).catch(() => {});
  }, [req, zoneId]);

  useEffect(() => {
    if (zoneId === null) return;
    (async () => {
      try { apply((await req(`/v2/zones/${zoneId}`)).zone); } catch (e: any) { setError(e.message); } finally { setLoading(false); }
    })();
  }, [req, zoneId]);

  const save = async (addMissing: boolean) => {
    setSaving(true); setError(null); setPendingUnknown(null);
    try {
      const body = { name, description: description.trim() || null, codes, polygons, driver_ids: driverIds, add_missing: addMissing };
      const r: SaveResult = zone
        ? await req(`/v2/zones/${zone.id}`, { method: "PUT", body })
        : await req("/v2/zones", { method: "POST", body });
      apply(r.zone);
      setResult(r);
      onSaved(`Zona «${r.zone.name}» guardada: ${plural(r.zone.codes.length, "CP", "CP")}${r.zone.polygons.length ? ` y ${plural(r.zone.polygons.length, "polígono", "polígonos")}` : ""}`);
    } catch (e: any) {
      if (Array.isArray(e?.unknown) && e.unknown.length) setPendingUnknown(e.unknown);
      setError(e.message);
    } finally { setSaving(false); }
  };

  const links = zone?.links;
  return (
    <Modal title={zone ? `Zona «${zone.name}»` : "Nueva zona"} onClose={close} width="1100px">
      {loading ? <p style={smallMutedStyle}>Cargando…</p> : (
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "12px" }}>
            <Input id="zone-name" label="Nombre" value={name} onChange={(e) => setName(e.target.value)} placeholder="Vicente López" />
            <Input id="zone-description" label="Descripción (opcional)" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Partido completo" />
          </div>
          <div>
            <div style={{ fontSize: "14px", fontWeight: 600, marginBottom: "6px", color: "var(--text-primary)" }}>Códigos postales</div>
            <p style={{ ...smallMutedStyle, margin: "0 0 8px" }}>
              Pegá la columna de una planilla: comas, espacios o saltos de línea; también CPA (B1636ABC) y rangos (1602-1609, toma los CP del
              catálogo que caen en el rango). Un CP puede estar en varias zonas.
            </p>
            <textarea id="zone-codes" style={textareaStyle} value={codes} onChange={(e) => setCodes(e.target.value)} placeholder={"1602-1609\n1636\n1638"} />
          </div>
          <div>
            <div style={{ fontSize: "14px", fontWeight: 600, marginBottom: "6px", color: "var(--text-primary)" }}>Polígonos (opcional)</div>
            <p style={{ ...smallMutedStyle, margin: "0 0 8px" }}>
              Para donde los CP no alcanzan. En una estantería, un paquete con ubicación que cae adentro va por el polígono aunque su CP diga
              otra cosa; sin ubicación o fuera de todo polígono decide el CP. Dentro de un depósito, los polígonos de estanterías distintas no
              se pueden pisar (pegarlos por el borde sí). En las tarifas cuentan solo si la Configuración lo pide; igual, dentro de una
              tarifa, zonas de tiers distintos no se pueden pisar.
            </p>
            <ZonePolygonEditor polygons={polygons} onChange={setPolygons} others={others} points={points} onDrawingChange={setDrawing} />
          </div>
          <div>
            <div style={{ fontSize: "14px", fontWeight: 600, marginBottom: "6px", color: "var(--text-primary)" }}>Choferes habituales (opcional)</div>
            <p style={{ ...smallMutedStyle, margin: "0 0 8px" }}>
              Quiénes suelen repartir esta zona. No asignan nada solos: si la Configuración lo pide, se sugieren al armar el reparto.
            </p>
            <DriverMultiPicker id="zone-drivers" drivers={drivers} value={driverIds} onChange={setDriverIds} />
          </div>

          {links && (links.shelves.length || links.tariffs.length) ? (
            <div style={{ ...panelStyle, borderColor: WARN }}>
              <b>Vinculada a</b> — al guardar, estos lugares se actualizan con los CP y los polígonos nuevos:
              <div style={{ marginTop: "6px" }}>
                {links.shelves.map((s) => <div key={`s${s.shelf_id}`}>Estantería <b>{s.code}</b> <span style={smallMutedStyle}>{s.name}</span></div>)}
                {links.tariffs.map((t) => <div key={`t${t.tariff_id}`}>Tarifa <b>{t.name ?? t.tariff_id}</b> <span style={smallMutedStyle}>como {t.tier}</span></div>)}
              </div>
            </div>
          ) : zone ? <div style={smallMutedStyle}>Todavía no está vinculada: se vincula desde Estanterías o desde Listas de precios.</div> : null}

          {error ? <div style={{ ...panelStyle, borderColor: "var(--danger)", color: "var(--danger)" }}>{error}</div> : null}
          {pendingUnknown ? (
            <div style={{ ...panelStyle, borderColor: WARN }}>
              <div>Estos códigos no están en el catálogo geográfico: <b>{pendingUnknown.join(", ")}</b>.</div>
              <div style={{ marginTop: "10px" }}><Button variant="secondary" onClick={() => save(true)} disabled={saving}>Agregarlos al catálogo y guardar</Button></div>
            </div>
          ) : null}
          {result ? (
            <div style={{ ...panelStyle, borderColor: "var(--success)" }}>
              <div><b>{plural(result.zone.codes.length, "código postal", "códigos postales")}</b>{result.zone.polygons.length ? <> y <b>{plural(result.zone.polygons.length, "polígono", "polígonos")}</b></> : null} en la zona.</div>
              {result.empty_ranges.length ? <div style={{ color: WARN }}>Estos rangos no tenían ningún CP del catálogo: {result.empty_ranges.join(", ")}</div> : null}
              {result.unknown.length ? <div>Agregados al catálogo geográfico: {result.unknown.join(", ")}</div> : null}
              <SyncReport synced={result.synced} />
            </div>
          ) : null}

          <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
            {drawing ? <span style={{ ...smallMutedStyle, color: WARN }}>Hay un polígono a medio dibujar: terminalo o descartalo antes de guardar.</span> : null}
            <Button variant="ghost" onClick={close}>Cerrar</Button>
            <Button onClick={() => save(false)} disabled={saving || !name.trim() || drawing}>{saving ? "Guardando…" : "Guardar zona"}</Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
